import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Table,
  Button,
  Tag,
  Space,
  message,
  Modal,
  Input,
  Popconfirm,
  Badge,
  Tabs,
  Descriptions,
  Drawer,
  Typography,
  Alert,
  InputNumber,
  Progress,
  Divider,
  Timeline,
  Tooltip,
  Card,
  Statistic,
  Row,
  Col,
} from "antd";
import {
  ToolOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  EyeOutlined,
  SendOutlined,
  ArrowRightOutlined,
  ThunderboltOutlined,
  GroupOutlined,
} from "@ant-design/icons";
import { serviceCenter } from "../api";
import PageHeader from "../components/common/PageHeader";
import { fmtCurrency, fmtDate } from "../utils/format";

const { Text } = Typography;

function statusTag(s) {
  if (s === "IN_SERVICE")
    return (
      <Tag color="orange" icon={<ToolOutlined />}>
        In Service
      </Tag>
    );
  if (s === "REPAIRED")
    return (
      <Tag color="green" icon={<CheckCircleOutlined />}>
        Repaired
      </Tag>
    );
  if (s === "SCRAPPED")
    return (
      <Tag color="red" icon={<DeleteOutlined />}>
        Scrapped
      </Tag>
    );
  return <Tag>{s}</Tag>;
}

function fmtDT(v) {
  if (!v) return "—";
  return new Date(v).toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function qtyPending(sc) {
  return Math.max(0, Number(sc.qty || 0) - Number(sc.qtyRepaired || 0));
}

// ─────────────────────────────────────────────────────────────────────────────
// Grouped card for one rental return (shown in "In Service" tab)
// ─────────────────────────────────────────────────────────────────────────────
function ReturnGroup({
  returnNo,
  returnId,
  items,
  onRepairItem,
  onRepairAll,
  onScrap,
  onViewItem,
}) {
  const totalQty = items.reduce((s, i) => s + Number(i.qty || 0), 0);
  const totalRepaired = items.reduce(
    (s, i) => s + Number(i.qtyRepaired || 0),
    0,
  );
  const totalPending = totalQty - totalRepaired;
  const pct = totalQty > 0 ? Math.round((totalRepaired / totalQty) * 100) : 0;
  const allDone = totalPending === 0;

  return (
    <Card
      size="small"
      style={{
        marginBottom: 16,
        borderLeft: `4px solid ${allDone ? "#52c41a" : "#fa8c16"}`,
        borderRadius: 8,
      }}
      title={
        <Space>
          <GroupOutlined style={{ color: "#1677ff" }} />
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontWeight: 700,
              color: "#1677ff",
              fontSize: 14,
            }}
          >
            {returnNo}
          </span>
          <Tag color="blue">
            {items.length} item type{items.length !== 1 ? "s" : ""}
          </Tag>
          <Tag color={allDone ? "green" : "orange"}>
            {allDone ? "✓ All repaired" : `${totalPending} pending`}
          </Tag>
        </Space>
      }
      extra={
        !allDone && (
          <Tooltip title="Mark ALL pending items from this return as repaired in one click">
            <Popconfirm
              title="Bulk Repair"
              description={`Return all ${totalPending} pending item(s) from ${returnNo} to storage?`}
              onConfirm={() => onRepairAll(returnId, returnNo)}
              okText="Yes, Return All"
            >
              <Button
                type="primary"
                size="small"
                icon={<ThunderboltOutlined />}
              >
                Return All to Storage
              </Button>
            </Popconfirm>
          </Tooltip>
        )
      }
    >
      {/* Progress bar for the whole group */}
      <div style={{ marginBottom: 10 }}>
        <Progress
          percent={pct}
          format={() => `${totalRepaired}/${totalQty} repaired`}
          strokeColor={allDone ? "#52c41a" : "#fa8c16"}
          size="small"
        />
      </div>

      {/* Item rows */}
      <Table
        size="small"
        scroll={{ x: "max-content" }}
        pagination={false}
        rowKey="id"
        dataSource={items}
        scroll={{ x: "max-content" }}
        columns={[
          {
            title: "Product",
            render: (_, r) => (
              <span className="mono" style={{ fontWeight: 600 }}>
                {r.product?.name}
              </span>
            ),
          },
          {
            title: "Unit",
            render: (_, r) => <Tag>{r.product?.unit}</Tag>,
            width: 70,
          },
          {
            title: "Received",
            dataIndex: "qty",
            width: 90,
            render: (v) => <Tag color="blue">{Number(v)}</Tag>,
          },
          {
            title: "Repaired",
            width: 90,
            render: (_, r) => (
              <Tag color="green">{Number(r.qtyRepaired || 0)}</Tag>
            ),
          },
          {
            title: "Pending",
            width: 90,
            render: (_, r) => {
              const p = qtyPending(r);
              return (
                <Tag color={p > 0 ? "orange" : "green"}>{p > 0 ? p : "✓"}</Tag>
              );
            },
          },
          {
            title: "Damage",
            dataIndex: "damageDescription",
            ellipsis: true,
            render: (v) => v || <Text type="secondary">—</Text>,
          },
          {
            title: "Charge",
            dataIndex: "customerCharge",
            width: 100,
            render: (v) =>
              Number(v) > 0 ? (
                <span className="mono" style={{ color: "red", fontSize: 12 }}>
                  ₹{Number(v).toLocaleString("en-IN")}
                </span>
              ) : (
                "—"
              ),
          },
          {
            title: "Status",
            dataIndex: "status",
            width: 110,
            render: statusTag,
          },
          {
            title: "Actions",
            key: "a",
            width: 190,
            render: (_, r) => {
              const pending = qtyPending(r);
              return (
                <Space size={4}>
                  <Button
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={() => onViewItem(r)}
                  >
                    View
                  </Button>
                  {r.status === "IN_SERVICE" && pending > 0 && (
                    <Button
                      size="small"
                      icon={<SendOutlined />}
                      type="primary"
                      ghost
                      onClick={() => onRepairItem(r)}
                    >
                      Return
                    </Button>
                  )}
                  {r.status === "IN_SERVICE" && (
                    <Button
                      size="small"
                      icon={<DeleteOutlined />}
                      danger
                      onClick={() => onScrap(r)}
                    >
                      Scrap
                    </Button>
                  )}
                </Space>
              );
            },
          },
        ]}
      />
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────
export default function ServiceCenterPage() {
  const [inService, setInService] = useState([]);
  const [allItems, setAllItems] = useState({ content: [], totalElements: 0 });
  const [loading, setLoading] = useState(false);
  const [viewRec, setViewRec] = useState(null);
  const [repairLogs, setRepairLogs] = useState([]);
  const [repairLogsLoading, setRepairLogsLoading] = useState(false);

  // Partial repair modal (per item)
  const [repairModal, setRepairModal] = useState(null);
  const [repairQty, setRepairQty] = useState(1);
  const [repairNotes, setRepairNotes] = useState("");
  const [repairSaving, setRepairSaving] = useState(false);

  // Scrap modal
  const [scrapModal, setScrapModal] = useState(null);
  const [scrapNotes, setScrapNotes] = useState("");
  const [page, setPage] = useState(0);

  // ── Data loading ──────────────────────────────────────────────────

  const loadInService = useCallback(async () => {
    try {
      const r = await serviceCenter.inService();
      setInService(r.data.data || []);
    } catch {
      /* silent */
    }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await serviceCenter.list({ page, size: 20 });
      setAllItems(r.data.data);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    loadInService();
    loadAll();
  }, [loadInService, loadAll]);

  // ── Group in-service items by rental return ───────────────────────

  const groupedByReturn = useMemo(() => {
    const map = {};
    inService.forEach((sc) => {
      const key = sc.rentalReturn?.id || "unknown";
      if (!map[key]) {
        map[key] = {
          returnId: sc.rentalReturn?.id,
          returnNo: sc.rentalReturn?.returnNo || "—",
          items: [],
        };
      }
      map[key].items.push(sc);
    });
    // Sort groups: those with pending items first
    return Object.values(map).sort((a, b) => {
      const aPending = a.items.reduce((s, i) => s + qtyPending(i), 0);
      const bPending = b.items.reduce((s, i) => s + qtyPending(i), 0);
      return bPending - aPending;
    });
  }, [inService]);

  // ── View drawer ───────────────────────────────────────────────────

  const openView = async (rec) => {
    setViewRec(rec);
    setRepairLogs([]);
    setRepairLogsLoading(true);
    try {
      const r = await serviceCenter.repairLogs(rec.id);
      setRepairLogs(r.data.data || []);
    } catch {
      /* silent */
    } finally {
      setRepairLogsLoading(false);
    }
  };

  // ── Partial repair per item ───────────────────────────────────────

  const openRepairModal = (rec) => {
    const pending = qtyPending(rec);
    setRepairModal(rec);
    setRepairQty(pending);
    setRepairNotes("");
  };

  const handleRepairPartial = async () => {
    setRepairSaving(true);
    try {
      await serviceCenter.repairPartial(repairModal.id, {
        qty: repairQty,
        notes: repairNotes || undefined,
      });
      const pending = qtyPending(repairModal);
      if (repairQty >= pending) {
        message.success(
          `All ${repairModal.product?.name} items returned to storage`,
        );
      } else {
        message.success(
          `${repairQty} × ${repairModal.product?.name} returned. ${pending - repairQty} still in service.`,
        );
      }
      setRepairModal(null);
      loadInService();
      loadAll();
      if (viewRec?.id === repairModal.id) openView({ ...repairModal });
    } catch (err) {
      message.error(err?.response?.data?.message || "Error saving repair");
    } finally {
      setRepairSaving(false);
    }
  };

  // ── Bulk repair for entire rental return ─────────────────────────

  const handleBulkRepaired = async (returnId, returnNo) => {
    try {
      await serviceCenter.bulkRepaired(returnId);
      message.success(`All items from ${returnNo} returned to storage`);
      loadInService();
      loadAll();
    } catch (err) {
      message.error(err?.response?.data?.message || "Error");
    }
  };

  // ── Scrap ─────────────────────────────────────────────────────────

  const handleScrap = async () => {
    try {
      await serviceCenter.markScrapped(scrapModal.id, {
        notes: scrapNotes || "Scrapped from service center",
      });
      message.success("Item scrapped and removed from inventory");
      setScrapModal(null);
      setScrapNotes("");
      loadInService();
      loadAll();
    } catch {
      message.error("Error");
    }
  };

  // ── "All History" flat table columns ─────────────────────────────

  const allCols = [
    {
      title: "Service No",
      dataIndex: "serviceNo",
      render: (v) => <span className="mono">{v}</span>,
    },
    {
      title: "From Return",
      render: (_, r) => (
        <span className="mono" style={{ color: "#1677ff", fontWeight: 600 }}>
          {r.rentalReturn?.returnNo || "—"}
        </span>
      ),
    },
    {
      title: "Product",
      render: (_, r) => <span className="mono">{r.product?.name}</span>,
    },
    {
      title: "Qty (Total / Repaired)",
      render: (_, r) => {
        const total = Number(r.qty || 0);
        const repaired = Number(r.qtyRepaired || 0);
        const pending = total - repaired;
        return (
          <Space size={4}>
            <Tag color="blue">{total}</Tag>
            <Tag color="green">+{repaired}</Tag>
            {pending > 0 && <Tag color="orange">{pending} left</Tag>}
          </Space>
        );
      },
    },
    {
      title: "Damage",
      dataIndex: "damageDescription",
      ellipsis: true,
      render: (v) => v || "—",
    },
    {
      title: "Charge",
      dataIndex: "customerCharge",
      render: (v) =>
        Number(v) > 0 ? `₹${Number(v).toLocaleString("en-IN")}` : "—",
    },
    { title: "Status", dataIndex: "status", render: statusTag },
    { title: "Received", dataIndex: "createdAt", render: fmtDT },
    { title: "Resolved", dataIndex: "repairedAt", render: fmtDT },
    {
      title: "",
      key: "a",
      render: (_, r) => (
        <Button size="small" icon={<EyeOutlined />} onClick={() => openView(r)}>
          View
        </Button>
      ),
    },
  ];

  // ── Summary stats for in-service tab header ───────────────────────

  const totalInService = inService.length;
  const totalPendingQty = inService.reduce((s, i) => s + qtyPending(i), 0);
  const uniqueReturns = groupedByReturn.length;

  const pendingForModal = repairModal ? qtyPending(repairModal) : 0;

  // ── Tabs ──────────────────────────────────────────────────────────

  const tabItems = [
    {
      key: "active",
      label: (
        <span>
          <ToolOutlined />
          In Service
          <Badge
            count={totalInService}
            style={{ marginLeft: 8, backgroundColor: "#fa8c16" }}
          />
        </span>
      ),
      children: (
        <>
          {/* Summary row */}
          {totalInService > 0 && (
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col>
                <Card
                  size="small"
                  style={{ minWidth: 130, textAlign: "center" }}
                >
                  <Statistic
                    title="Rental Returns"
                    value={uniqueReturns}
                    valueStyle={{ color: "#1677ff", fontSize: 22 }}
                  />
                </Card>
              </Col>
              <Col>
                <Card
                  size="small"
                  style={{ minWidth: 130, textAlign: "center" }}
                >
                  <Statistic
                    title="Item Types"
                    value={totalInService}
                    valueStyle={{ color: "#fa8c16", fontSize: 22 }}
                  />
                </Card>
              </Col>
              <Col>
                <Card
                  size="small"
                  style={{ minWidth: 130, textAlign: "center" }}
                >
                  <Statistic
                    title="Units Pending"
                    value={totalPendingQty}
                    valueStyle={{ color: "#f5222d", fontSize: 22 }}
                  />
                </Card>
              </Col>
            </Row>
          )}

          {inService.length === 0 ? (
            <Alert
              type="success"
              message="No items currently in service center — all clear!"
            />
          ) : (
            groupedByReturn.map((group) => (
              <ReturnGroup
                key={group.returnId}
                returnId={group.returnId}
                returnNo={group.returnNo}
                items={group.items}
                onRepairItem={openRepairModal}
                onRepairAll={handleBulkRepaired}
                onScrap={(r) => {
                  setScrapModal(r);
                  setScrapNotes("");
                }}
                onViewItem={openView}
              />
            ))
          )}
        </>
      ),
    },
    {
      key: "all",
      label: "All History",
      children: (
        <Table
          scroll={{ x: "max-content" }}
          columns={allCols}
          dataSource={allItems.content}
          rowKey="id"
          loading={loading}
          size="small"
          pagination={{
            total: allItems.totalElements,
            pageSize: 20,
            current: page + 1,
            onChange: (p) => setPage(p - 1),
          }}
        />
      ),
    },
  ];

  // ── Render ────────────────────────────────────────────────────────

  return (
    <div>
      <PageHeader
        title="Service Center"
        subtitle="Damaged rental items grouped by rental return — track repair progress per batch"
      />

      <Tabs items={tabItems} defaultActiveKey="active" />

      {/* ── Partial Repair Modal (per item) ──────────────────────── */}
      <Modal
        open={!!repairModal}
        title={
          <span>
            <SendOutlined style={{ marginRight: 8, color: "#52c41a" }} />
            Return Items to Storage
          </span>
        }
        onCancel={() => setRepairModal(null)}
        onOk={handleRepairPartial}
        okText="Confirm Return to Storage"
        okButtonProps={{
          loading: repairSaving,
          disabled: !repairQty || repairQty <= 0,
        }}
        width={480}
      >
        {repairModal && (
          <>
            <Descriptions
              bordered
              size="small"
              column={1}
              style={{ marginBottom: 14 }}
            >
              <Descriptions.Item label="From Return">
                <span
                  className="mono"
                  style={{ color: "#1677ff", fontWeight: 700 }}
                >
                  {repairModal.rentalReturn?.returnNo || "—"}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="Product">
                <strong>{repairModal.product?.name}</strong>
                <Tag style={{ marginLeft: 8 }}>{repairModal.product?.unit}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Total Received">
                <Tag color="blue">{Number(repairModal.qty)}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Already Returned to Storage">
                <Tag color="green">{Number(repairModal.qtyRepaired || 0)}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Still Pending">
                <Tag color="orange">{pendingForModal}</Tag>
              </Descriptions.Item>
            </Descriptions>

            <div style={{ marginBottom: 12 }}>
              <div
                style={{
                  fontSize: 12,
                  color: "var(--text-muted)",
                  marginBottom: 6,
                }}
              >
                How many are you returning to storage <strong>now</strong>?
                <span style={{ color: "#fa8c16", marginLeft: 6 }}>
                  (max {pendingForModal})
                </span>
              </div>
              <InputNumber
                min={1}
                max={pendingForModal}
                step={1}
                value={repairQty}
                onChange={(v) => setRepairQty(v)}
                style={{ width: "100%" }}
                size="large"
                addonBefore="Qty"
              />
              {repairQty >= pendingForModal ? (
                <Alert
                  type="success"
                  style={{ marginTop: 8 }}
                  message="This will close the service record — all items returned to storage."
                />
              ) : (
                <Alert
                  type="info"
                  style={{ marginTop: 8 }}
                  message={`${pendingForModal - repairQty} item(s) will remain in service after this dispatch.`}
                />
              )}
            </div>

            <Input.TextArea
              rows={2}
              placeholder="Notes (optional — e.g. 'First batch', 'After welding repair')"
              value={repairNotes}
              onChange={(e) => setRepairNotes(e.target.value)}
            />
          </>
        )}
      </Modal>

      {/* ── Scrap Confirmation Modal ──────────────────────────────── */}
      <Modal
        open={!!scrapModal}
        title={
          <span style={{ color: "red" }}>
            <DeleteOutlined style={{ marginRight: 8 }} />
            Scrap Item
          </span>
        }
        onCancel={() => setScrapModal(null)}
        onOk={handleScrap}
        okText="Confirm Scrap"
        okButtonProps={{ danger: true }}
      >
        {scrapModal && (
          <>
            <p>
              You are about to scrap{" "}
              <strong>
                {scrapModal.qty} × {scrapModal.product?.name}
              </strong>{" "}
              from return{" "}
              <span className="mono" style={{ color: "#1677ff" }}>
                {scrapModal.rentalReturn?.returnNo}
              </span>
              . This creates a scrap entry and permanently deducts the quantity
              from inventory.
            </p>
            <Input.TextArea
              rows={2}
              placeholder="Scrap notes (optional)"
              value={scrapNotes}
              onChange={(e) => setScrapNotes(e.target.value)}
            />
          </>
        )}
      </Modal>

      {/* ── View Detail Drawer ────────────────────────────────────── */}
      <Drawer
        title={`Service Record: ${viewRec?.serviceNo}`}
        open={!!viewRec}
        onClose={() => setViewRec(null)}
        width={540}
      >
        {viewRec && (
          <>
            <Descriptions
              bordered
              size="small"
              column={1}
              style={{ marginBottom: 16 }}
            >
              <Descriptions.Item label="Service No">
                <span className="mono">{viewRec.serviceNo}</span>
              </Descriptions.Item>
              <Descriptions.Item label="From Rental Return">
                <span
                  className="mono"
                  style={{ color: "#1677ff", fontWeight: 700, fontSize: 13 }}
                >
                  {viewRec.rentalReturn?.returnNo || "—"}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="Product">
                <strong>{viewRec.product?.name}</strong>
                <Tag style={{ marginLeft: 8 }}>{viewRec.product?.unit}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Qty Received">
                <Tag color="blue">{Number(viewRec.qty)}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Qty Returned to Storage">
                <Tag color="green">{Number(viewRec.qtyRepaired || 0)}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Qty Still Pending">
                {(() => {
                  const p = qtyPending(viewRec);
                  return (
                    <Tag color={p > 0 ? "orange" : "green"}>
                      {p > 0 ? p : "✓ All done"}
                    </Tag>
                  );
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                {statusTag(viewRec.status)}
              </Descriptions.Item>
              <Descriptions.Item label="Damage">
                {viewRec.damageDescription || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Customer Charge">
                {Number(viewRec.customerCharge) > 0 ? (
                  <span style={{ color: "red", fontWeight: 600 }}>
                    ₹{Number(viewRec.customerCharge).toLocaleString("en-IN")}
                  </span>
                ) : (
                  "—"
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Received at Service Center">
                {fmtDT(viewRec.createdAt)}
              </Descriptions.Item>
              {viewRec.repairedAt && (
                <Descriptions.Item label="Fully Resolved">
                  {fmtDT(viewRec.repairedAt)}
                </Descriptions.Item>
              )}
            </Descriptions>

            {/* Progress bar */}
            {Number(viewRec.qty) > 0 && (
              <div style={{ marginBottom: 16 }}>
                <Progress
                  percent={Math.round(
                    (Number(viewRec.qtyRepaired || 0) / Number(viewRec.qty)) *
                      100,
                  )}
                  format={() =>
                    `${Number(viewRec.qtyRepaired || 0)} / ${Number(viewRec.qty)} repaired`
                  }
                  strokeColor={
                    qtyPending(viewRec) === 0 ? "#52c41a" : "#fa8c16"
                  }
                />
              </div>
            )}

            {/* Repair dispatch timeline */}
            <Divider orientation="left" style={{ color: "white" }}>
              <ToolOutlined style={{ marginRight: 6 }} />
              Repair Dispatch History
            </Divider>

            {repairLogsLoading ? (
              <div
                style={{
                  textAlign: "center",
                  padding: 24,
                  color: "var(--text-muted)",
                }}
              >
                Loading…
              </div>
            ) : repairLogs.length === 0 ? (
              <Alert type="info" message="No repair dispatches recorded yet." />
            ) : (
              <Timeline
                items={repairLogs.map((log, i) => ({
                  color: "green",
                  dot: <ArrowRightOutlined style={{ color: "#52c41a" }} />,
                  children: (
                    <div>
                      <Space wrap>
                        <Tag
                          color="green"
                          style={{ fontFamily: "var(--font-mono)" }}
                        >
                          {Number(log.qtyDispatched)} {viewRec.product?.unit} →
                          Storage
                        </Tag>
                        <Text
                          type="secondary"
                          style={{ fontSize: 11, color: "#52c41a" }}
                        >
                          {fmtDT(log.dispatchedAt)}
                        </Text>
                        <Text
                          type="secondary"
                          style={{ fontSize: 11, color: "#52c41a" }}
                        >
                          Dispatch #{i + 1}
                        </Text>
                      </Space>
                      {log.notes && (
                        <div
                          style={{
                            fontSize: 12,
                            color: "var(--text-muted)",
                            marginTop: 2,
                          }}
                        >
                          {log.notes}
                        </div>
                      )}
                    </div>
                  ),
                }))}
              />
            )}

            {/* Action buttons at bottom of drawer */}
            {viewRec.status === "IN_SERVICE" && qtyPending(viewRec) > 0 && (
              <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
                <Button
                  icon={<SendOutlined />}
                  type="primary"
                  onClick={() => openRepairModal(viewRec)}
                >
                  Return to Storage
                </Button>
                <Button
                  icon={<DeleteOutlined />}
                  danger
                  onClick={() => {
                    setViewRec(null);
                    setScrapModal(viewRec);
                    setScrapNotes("");
                  }}
                >
                  Scrap
                </Button>
              </div>
            )}
          </>
        )}
      </Drawer>
    </div>
  );
}
